<template>
    <div class="h-screen">
        <!-- 모바일 오버레이 -->
        <div v-if="isMobileMenuOpen" class="fixed inset-0 bg-gray-600 bg-opacity-75 transition-opacity lg:hidden"
            @click="closeMobileMenu"></div>

        <!-- 사이드바 -->
        <div :class="[
            'fixed inset-y-0 left-0 z-30 bg-gradient-to-b from-blue-700 to-blue-900 transform transition-all duration-300 ease-in-out lg:relative',
            isSidebarOpen ? 'w-56' : 'w-16',
            !isSidebarOpen && isMobileMenuOpen ? 'w-56' : '',
            isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
            'h-screen'
        ]">
            <div class="flex items-center justify-between p-3">
                <div class="flex items-center cursor-pointer" @click="openSidebar">
                    <div class="w-8 h-8 bg-white rounded-full flex items-center justify-center">
                        <LayoutDashboardIcon size="20" class="text-blue-700" />
                    </div>
                    <h1
                        :class="['font-bold ml-2 text-sm transition-opacity duration-300', isSidebarOpen ? 'opacity-100' : 'opacity-0 w-0']">
                        매니저용 매장관리웹</h1>
                </div>
                <div class="flex">
                    <!-- 모바일 닫기 버튼 -->
                    <button @click="closeMobileMenu"
                        class="lg:hidden text-white focus:outline-none hover:bg-blue-600 rounded-full p-1">
                        <XIcon size="24" />
                    </button>
                    <!-- 데스크톱 토글 버튼 -->
                    <button @click="toggleSidebar"
                        class="hidden lg:block text-white focus:outline-none hover:bg-blue-600 rounded-full p-1">
                        <ChevronLeftIcon v-if="isSidebarOpen" size="24" />
                        <ChevronRightIcon v-else size="24" />
                    </button>
                </div>
            </div>
            <nav class="mt-3">
                <ul>
                    <li v-for="(item, index) in filteredMenuItems" :key="index" class="mb-1 px-3">
                        <div @click="handleMenuItemClick(index)" :class="[
                            'flex items-center py-1.5 px-2 rounded-lg cursor-pointer transition-colors duration-200 text-sm',
                            item.isActive ? 'bg-blue-600 text-white' : 'text-blue-100 hover:bg-blue-800'
                        ]">
                            <component :is="getIcon(item.icon)" size="18" />
                            <span
                                :class="['ml-3 transition-all duration-300', isSidebarOpen ? 'opacity-100 inline-block' : 'opacity-0 hidden']">{{
                                    item.name }}</span>
                            <ChevronDownIcon v-if="isSidebarOpen && item.submenu" :class="[
                                'ml-auto transition-transform duration-200',
                                item.isOpen ? 'transform rotate-180' : ''
                            ]" size="14" />
                        </div>
                        <transition enter-active-class="transition-all duration-300 ease-in-out"
                            enter-from-class="opacity-0 max-h-0" enter-to-class="opacity-100 max-h-screen"
                            leave-active-class="transition-all duration-300 ease-in-out"
                            leave-from-class="opacity-100 max-h-screen" leave-to-class="opacity-0 max-h-0">
                            <ul v-if="isSidebarOpen && item.submenu && item.isOpen" class="mt-1 space-y-0.5">
                                <li v-for="(subItem, subIndex) in item.submenu" :key="subItem.name"
                                    @click.stop="handleSubMenuItemClick(index, subIndex)" :class="[
                                        'px-6 py-1.5 rounded-lg cursor-pointer transition-colors duration-200 text-sm',
                                        subItem.isActive ? 'bg-blue-700 text-white' : 'text-blue-200 hover:bg-blue-800'
                                    ]">
                                    {{ subItem.name }}
                                </li>
                            </ul>
                        </transition>
                    </li>
                </ul>
            </nav>
        </div>
    </div>
</template>

<script setup>
import { ref, watch, onMounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import {
    ChevronLeftIcon,
    ChevronRightIcon,
    HomeIcon,
    UsersIcon,
    ChevronDownIcon,
    LayoutDashboardIcon,
    FileTextIcon,
    BarChartIcon,
    XIcon,
    MessageSquareIcon,
    DownloadIcon,
    ReceiptIcon
} from 'lucide-vue-next'
import { useAuthStore } from '../../stores/auth'

const props = defineProps({
    modelValue: {
        type: Boolean,
        default: true
    },
    isMobileMenuOpen: {
        type: Boolean,
        default: false
    }
})

const emit = defineEmits(['update:modelValue', 'update:isMobileMenuOpen'])

const router = useRouter()
const authStore = useAuthStore()

const isSidebarOpen = ref(props.modelValue)

const menuItems = ref([
    /* 원래 메뉴 구조
    {
        name: 'Dashboard',
        icon: 'HomeIcon',
        submenu: null,
        isActive: true
    },
    {
        name: 'Users',
        icon: 'UsersIcon',
        isOpen: false,
        isActive: false,
        submenu: [
            { name: 'List Users', isActive: false },
            { name: 'Add User', isActive: false },
            { name: 'User Roles', isActive: false }
        ]
    },
    {
        name: 'Reports',
        icon: 'FileTextIcon',
        isOpen: false,
        isActive: false,
        submenu: [
            { name: 'Sales Report', isActive: false },
            { name: 'User Activity', isActive: false }
        ]
    },
    {
        name: 'Analytics',
        icon: 'BarChartIcon',
        submenu: null,
        isActive: false
    },
    {
        name: 'Settings',
        icon: 'SettingsIcon',
        isOpen: false,
        isActive: false,
        submenu: [
            { name: 'General', isActive: false },
            { name: 'Security', isActive: false },
            { name: 'Notifications', isActive: false }
        ]
    }
    */
    
    // 새로운 메뉴 구조
    {
        name: '대시보드',
        icon: 'HomeIcon',
        submenu: null,
        isActive: true
    },
    {
        name: '공지사항',
        icon: 'FileTextIcon',
        submenu: null,
        isActive: false
    },
    {
        name: 'VMD 관리',
        icon: 'LayoutDashboardIcon',
        isOpen: false,
        isActive: false,
        submenu: [
            { name: '요청사항', isActive: false },
            { name: '제출목록', isActive: false }
        ]
    },
    {
        name: '문의하기',
        icon: 'MessageSquareIcon',
        submenu: null,
        isActive: false
    },
    // {
    //     name: '중간관리자',
    //     icon: 'UsersIcon',
    //     submenu: null,
    //     isActive: false
    // },
    {
        name: '중간관리자 세금계산서',
        icon: 'ReceiptIcon',
        submenu: null,
        isActive: false,
        show: () => authStore.user?.shop_gb != 1
    },
    {
        name: '상품이미지 다운로드',
        icon: 'DownloadIcon',
        submenu: null,
        isActive: false
    }
])

const filteredMenuItems = computed(() => {
    return menuItems.value.filter(item => !item.show || item.show())
})

/* 원래 아이콘 맵
const iconMap = {
    HomeIcon,
    UsersIcon,
    SettingsIcon,
    FileTextIcon,
    BarChartIcon
}
*/

// 새로운 아이콘 맵
const iconMap = {
    HomeIcon,
    UsersIcon,
    FileTextIcon,
    BarChartIcon,
    LayoutDashboardIcon,
    MessageSquareIcon,
    DownloadIcon,
    ReceiptIcon
}

const getIcon = (iconName) => {
    return iconMap[iconName]
}

const toggleSidebar = () => {
    isSidebarOpen.value = !isSidebarOpen.value
    emit('update:modelValue', isSidebarOpen.value)
}

const openSidebar = () => {
    if (!isSidebarOpen.value) {
        isSidebarOpen.value = true
        emit('update:modelValue', true)
    }
}

const handleMenuItemClick = (index) => {
    if (!isSidebarOpen.value) {
        isSidebarOpen.value = true
        emit('update:modelValue', true)
    }

    const item = filteredMenuItems.value[index]
    
    // 메뉴 항목 활성화 상태 업데이트
    menuItems.value.forEach((menuItem) => {
        const menuItemIndex = menuItems.value.findIndex(m => m.name === menuItem.name)
        menuItem.isActive = menuItemIndex === index
        if (menuItem.submenu) {
            menuItem.submenu.forEach(subItem => {
                subItem.isActive = false
            })
        }
    })

    // 서브메뉴가 있는 경우 토글
    if (item.submenu) {
        item.isOpen = !item.isOpen
    } else {
        // 서브메뉴가 없는 경우 라우팅 처리
        switch (item.name) {
            case '대시보드':
                router.push('/')
                break
            case '공지사항':
                router.push('/notice')
                break
            case '문의하기':
                router.push('/inquiry')
                break
            case '중간관리자 세금계산서':
                router.push('/tax-login')
                break
            case '상품이미지 다운로드':
                router.push('/webhard-download')
                break
        }
        // 모바일 메뉴 닫기
        emit('update:isMobileMenuOpen', false)
    }
}

const handleSubMenuItemClick = (index, subIndex) => {
    const item = menuItems.value[index]
    const subItem = item.submenu[subIndex]
    
    // 서브메뉴 항목 활성화 상태 업데이트
    item.submenu.forEach((subMenuItem, i) => {
        subMenuItem.isActive = i === subIndex
    })

    // 라우팅 처리
    switch (subItem.name) {
        case '요청사항':
            router.push('/vmd/requests')
            break
        case '제출목록':
            router.push('/vmd/submissions')
            break
    }
    // 모바일 메뉴 닫기
    emit('update:isMobileMenuOpen', false)
}

const closeMobileMenu = () => {
    emit('update:isMobileMenuOpen', false)
}

const updateActiveMenu = () => {
    const currentPath = router.currentRoute.value.path
    
    menuItems.value.forEach((menuItem) => {
        menuItem.isActive = false
        if (menuItem.submenu) {
            menuItem.submenu.forEach((subItem) => {
                subItem.isActive = false
            })
        }
    })

    switch(currentPath) {
        case '/':
            menuItems.value[0].isActive = true // 대시보드
            break
        case '/notice':
            menuItems.value[1].isActive = true // 공지사항
            break
        case '/inquiry': {
            // 문의하기 메뉴 활성화
            const inquiryIndex = menuItems.value.findIndex(item => item.name === '문의하기')
            if (inquiryIndex !== -1) {
                menuItems.value[inquiryIndex].isActive = true
            }
            break
        }
        case '/vmd/requests': {
            // VMD 관리 메뉴 활성화
            const vmdIndex = menuItems.value.findIndex(item => item.name === 'VMD 관리')
            if (vmdIndex !== -1) {
                menuItems.value[vmdIndex].isActive = true
                menuItems.value[vmdIndex].isOpen = true
                // 요청사항 서브메뉴 활성화
                const requestIndex = menuItems.value[vmdIndex].submenu.findIndex(item => item.name === '요청사항')
                if (requestIndex !== -1) {
                    menuItems.value[vmdIndex].submenu[requestIndex].isActive = true
                }
            }
            break
        }
        case '/vmd/submissions': {
            // VMD 관리 메뉴 활성화
            const vmdIndex = menuItems.value.findIndex(item => item.name === 'VMD 관리')
            if (vmdIndex !== -1) {
                menuItems.value[vmdIndex].isActive = true
                menuItems.value[vmdIndex].isOpen = true
                // 제출목록 서브메뉴 활성화
                const submissionIndex = menuItems.value[vmdIndex].submenu.findIndex(item => item.name === '제출목록')
                if (submissionIndex !== -1) {
                    menuItems.value[vmdIndex].submenu[submissionIndex].isActive = true
                }
            }
            break
        }
        // 필요한 다른 경로들도 추가
    }
}

// 라우터 경로 변경 감시
watch(() => router.currentRoute.value.path, () => {
    updateActiveMenu()
})

// 컴포넌트 마운트 시 초기 메뉴 상태 설정
onMounted(() => {
    updateActiveMenu()
})
</script>